import React from 'react';
import PropTypes from 'prop-types';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    MenuItem,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';

/**
 * FilterDrawer
 *
 * Renders a right-side drawer containing dynamic filter fields based on `filterConfig`.
 * Supported field types: string, number, boolean, select, multiselect, dateRange.
 * - dateRange uses two pickers (from/to). Configuration options:
 *   Preferred:  { type: 'dateRange', label, fromKey: 'start_date', toKey: 'end_date', key: 'date_range' }
 *   Backward compatible: { type: 'dateRange', label, keys: { from: 'from', to: 'to' }, key: 'date_range' }
 *   The `key` is still used for activation selection; the actual field names sent upstream are fromKey/toKey (or keys.from/keys.to fallback).
 */
export const FilterDrawer = ({ open, onClose, filterConfig = [], values, onChange, onApply, onReset, activeKeys, onActiveKeysChange }) => {
    const handleFieldChange = (key, val) => {
        onChange({ ...values, [key]: val });
    };

    // Collect activation keys (still the explicit cfg.key). Keep for potential future validation.
    const allKeys = filterConfig.map(c => c.key || (c.keys && `${c.keys.from}_${c.keys.to}`));

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 380 } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" p={2} borderBottom="1px solid #eee">
                <Typography variant="h6">Filters</Typography>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>
            <Box p={2} display="flex" flexDirection="column" gap={2} flexGrow={1}>
                <TextField
                    label="Select Filters"
                    select
                    fullWidth
                    size="small"
                    SelectProps={{ multiple: true }}
                    value={activeKeys}
                    onChange={(e) => onActiveKeysChange(e.target.value)}
                >
                    {filterConfig.map(cfg => (
                        <MenuItem key={cfg.key || cfg.label} value={cfg.key}>{cfg.label}</MenuItem>
                    ))}
                </TextField>
                {filterConfig.filter(cfg => activeKeys.includes(cfg.key)).map(cfg => {
                    const { type, label, key, options } = cfg;
                    // Resolve date range internal keys (fromKey/toKey) with backward compatibility
                    const resolvedFromKey = cfg.fromKey || (cfg.keys && cfg.keys.from);
                    const resolvedToKey = cfg.toKey || (cfg.keys && cfg.keys.to);
                    switch (type) {
                        case 'string':
                        case 'number':
                            return (
                                <TextField
                                    key={key}
                                    label={label}
                                    type={type === 'number' ? 'number' : 'text'}
                                    fullWidth
                                    size="small"
                                    value={values[key] ?? ''}
                                    onChange={(e) => handleFieldChange(key, e.target.value)}
                                />
                            );
                        case 'boolean':
                            return (
                                <FormControlLabel
                                    key={key}
                                    control={<Checkbox checked={!!values[key]} onChange={(e) => handleFieldChange(key, e.target.checked)} />}
                                    label={label}
                                />
                            );
                        case 'select':
                            return (
                                <TextField
                                    key={key}
                                    label={label}
                                    select
                                    fullWidth
                                    size="small"
                                    value={values[key] ?? ''}
                                    onChange={(e) => handleFieldChange(key, e.target.value)}
                                >
                                    {(options || []).map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                </TextField>
                            );
                        case 'multiselect':
                            return (
                                <TextField
                                    key={key}
                                    label={label}
                                    select
                                    fullWidth
                                    size="small"
                                    SelectProps={{ multiple: true }}
                                    value={values[key] ?? []}
                                    onChange={(e) => handleFieldChange(key, e.target.value)}
                                >
                                    {(options || []).map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                                </TextField>
                            );
                        case 'dateRange':
                            if (!resolvedFromKey || !resolvedToKey) {
                                return (
                                    <Box key={label} display="flex" flexDirection="column" gap={1}>
                                        <Typography variant="subtitle2" color="error">{label} (invalid dateRange config)</Typography>
                                        <Typography variant="caption">Missing fromKey/toKey (or keys.from/keys.to)</Typography>
                                    </Box>
                                );
                            }
                            return (
                                <Box key={label} display="flex" flexDirection="column" gap={1}>
                                    <Typography variant="subtitle2">{label}</Typography>
                                    <TextField
                                        label="From"
                                        type="date"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        value={values[resolvedFromKey] ? dayjs(values[resolvedFromKey]).format('YYYY-MM-DD') : ''}
                                        onChange={(e) => handleFieldChange(resolvedFromKey, e.target.value)}
                                    />
                                    <TextField
                                        label="To"
                                        type="date"
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        value={values[resolvedToKey] ? dayjs(values[resolvedToKey]).format('YYYY-MM-DD') : ''}
                                        onChange={(e) => handleFieldChange(resolvedToKey, e.target.value)}
                                    />
                                </Box>
                            );
                        default:
                            return null;
                    }
                })}
            </Box>
            <Box p={2} borderTop="1px solid #eee" display="flex" gap={2}>
                <Button variant="outlined" onClick={() => { onReset && onReset(); }} fullWidth>Reset</Button>
                <Button variant="contained" onClick={() => { onApply && onApply(values); }} fullWidth>Apply</Button>
            </Box>
        </Drawer>
    );
};

FilterDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    filterConfig: PropTypes.arrayOf(PropTypes.object),
    values: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onApply: PropTypes.func,
    onReset: PropTypes.func,
    activeKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
    onActiveKeysChange: PropTypes.func.isRequired,
};

export default FilterDrawer;
